import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getCanManageUsers,
  getAllUsersForManagement,
  createUser,
  updateUser,
  CreateUserRequest,
  UpdateUserRequest,
} from '@/pages/users/api';
import { ASSET_LIBRARY_PERMISSIONS_QUERY_KEY } from '@/pages/asset-library/useAssetLibraryPermissions';

/**
 * Hook to check if the current user can manage other users.
 * Returns true if the user is in user_managers.yml
 */
export const useCanManageUsers = () => {
  return useQuery({
    queryKey: ['users', 'can-manage'],
    queryFn: getCanManageUsers,
    staleTime: 10 * 60 * 1000, // 10 minutes
    select: (data) => data.can_manage_users,
  });
};

/**
 * Hook to fetch all users for the management UI.
 * Only accessible to user managers.
 */
export const useUsersForManagement = () => {
  return useQuery({
    queryKey: ['users', 'management'],
    queryFn: getAllUsersForManagement,
    staleTime: 30 * 1000, // 30 seconds - users list can change
  });
};

/**
 * Hook to create a new user.
 */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequest) => createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'management'] });
    },
  });
};

/**
 * Hook to update an existing user.
 * Use with { is_active: false } to deactivate, { is_active: true } to reactivate.
 */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UpdateUserRequest }) =>
      updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', 'management'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
      // A role change moves Asset Library's permissions too, from its own matrix.
      // Removed rather than invalidated: Asset Library waits for its
      // permissions before fetching anything, but a stale cached copy counts
      // as loaded, so it would go ahead under the old role — asking a
      // downgraded caller's tab for `status=staged` and getting a 403.
      // Removing it makes the next visit a genuine first load.
      queryClient.removeQueries({ queryKey: ASSET_LIBRARY_PERMISSIONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ['users', 'can-manage'] });
      // Refresh the current user (used by useCurrentUser) so changes to the
      // editing user's own channel/market assignments take effect immediately
      // instead of waiting out the 5-minute staleTime.
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] });
    },
  });
};
